import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { User, Bicycle, PencilSimple, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { VEHICLE_TYPES } from '../lib/vehicleTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VEHICLES = VEHICLE_TYPES.map(v => ({ id: v.id, name: v.label }));

export default function Profile() {
    const { user, updateUser, logout } = useAuth();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        username: user?.username || '',
        vehicle_type: user?.vehicle_type || 'ebike',
    });

    const getInitials = () => {
        if (user?.first_name) {
            return user.first_name.charAt(0) + (user.last_name?.charAt(0) || '');
        }
        return user?.email?.charAt(0).toUpperCase() || 'U';
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API}/rider/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                const updatedUser = await res.json();
                updateUser(updatedUser);
                setEditing(false);
                toast.success('Profile updated!');
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to update profile');
            }
        } catch (error) {
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            username: user?.username || '',
            vehicle_type: user?.vehicle_type || 'ebike',
        });
        setEditing(false);
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <span className="label-text text-primary text-glow">SETTINGS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Profile</h1>
                </motion.div>

                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="glass-card mb-6">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4 mb-6">
                                <Avatar className="w-20 h-20 border-4 border-primary shadow-[0_0_15px_rgba(30,144,255,0.3)]">
                                    <AvatarImage src={user?.picture} />
                                    <AvatarFallback className="bg-primary/20 text-primary text-2xl font-bold font-display">
                                        {getInitials()}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <h2 className="text-xl font-bold">
                                        {user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Rider'}
                                    </h2>
                                    <p className="text-muted-foreground">{user?.email}</p>
                                    <p className="text-sm text-primary font-medium mt-1">
                                        Level {user?.level || 1} Rider
                                    </p>
                                </div>
                            </div>

                            {!editing ? (
                                <Button 
                                    onClick={() => setEditing(true)}
                                    variant="outline"
                                    className="w-full"
                                    data-testid="edit-profile-btn"
                                >
                                    <PencilSimple size={18} className="mr-2" />
                                    Edit Profile
                                </Button>
                            ) : (
                                <div className="flex gap-2">
                                    <Button 
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="flex-1 btn-primary"
                                        data-testid="save-profile-btn"
                                    >
                                        {saving ? 'Saving...' : (
                                            <>
                                                <Check size={18} className="mr-2" />
                                                Save
                                            </>
                                        )}
                                    </Button>
                                    <Button 
                                        onClick={handleCancel}
                                        variant="outline"
                                        className="flex-1"
                                        data-testid="cancel-edit-btn"
                                    >
                                        <X size={18} className="mr-2" />
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Edit Form */}
                {editing && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Card className="glass-card mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User size={20} />
                                    Personal Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="first_name">First Name</Label>
                                        <Input
                                            id="first_name"
                                            value={formData.first_name}
                                            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                            placeholder="John"
                                            data-testid="input-first-name"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="last_name">Last Name</Label>
                                        <Input
                                            id="last_name"
                                            value={formData.last_name}
                                            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                            placeholder="Doe"
                                            data-testid="input-last-name"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="username">Username</Label>
                                    <Input
                                        id="username"
                                        value={formData.username}
                                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                                        placeholder="johndoe"
                                        data-testid="input-username"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="glass-card mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Bicycle size={20} />
                                    Vehicle Settings
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Label htmlFor="vehicle">Primary Vehicle</Label>
                                <Select 
                                    value={formData.vehicle_type} 
                                    onValueChange={(value) => setFormData({...formData, vehicle_type: value})}
                                >
                                    <SelectTrigger data-testid="select-vehicle">
                                        <SelectValue placeholder="Select vehicle" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VEHICLES.map((v) => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card className="glass-card mb-6">
                        <CardHeader>
                            <CardTitle>Your Stats</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-center p-4 bg-muted/50 rounded-xl">
                                    <p className="stat-number text-2xl">{user?.total_rides || 0}</p>
                                    <p className="label-text mt-1">Total Rides</p>
                                </div>
                                <div className="text-center p-4 bg-muted/50 rounded-xl">
                                    <p className="stat-number text-2xl">{user?.total_distance?.toFixed(1) || '0.0'}</p>
                                    <p className="label-text mt-1">KM Ridden</p>
                                </div>
                                <div className="text-center p-4 bg-muted/50 rounded-xl">
                                    <p className="stat-number text-2xl">{user?.level || 1}</p>
                                    <p className="label-text mt-1">Level</p>
                                </div>
                                <div className="text-center p-4 bg-muted/50 rounded-xl">
                                    <p className="stat-number text-2xl text-primary">{user?.boltz_balance?.toFixed(0) || '0'}</p>
                                    <p className="label-text mt-1">Boltz Balance</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Sign Out */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Button 
                        onClick={logout}
                        variant="outline"
                        className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid="logout-btn"
                    >
                        Sign Out
                    </Button>
                </motion.div>
            </div>
        </Layout>
    );
}
